import { api } from "../base";
import type { IMarkupTier } from "@pulse-ui/codegen/generated_ts/markup_tier";
import type { IClstAccountTier } from "@pulse-ui/codegen/generated_ts/clst_account_tier";

// ── Types ───────────────────────────────────────────────────────────────────

export type MarkupTier = IMarkupTier;
export type AccountTier = IClstAccountTier;

export interface MarkupTierOption {
  code: string;
  display_name: string;
}

// ── Endpoints ───────────────────────────────────────────────────────────────

const tradeEndpoints = api.injectEndpoints({
  endpoints: (build) => ({
    // ── Markup Tiers ──────────────────────────────────────────────────

    /** Fetches all markup tiers. */
    getMarkupTiers: build.query<IMarkupTier[], void>({
      query: () => ({ url: "/api/trade/v1/markup-tier" }),
      providesTags: ["MarkupTiers"],
    }),

    /** Creates a markup tier. */
    createMarkupTier: build.mutation<void, Omit<IMarkupTier, "id">>({
      query: (tier) => ({
        url: "/api/trade/v1/markup-tier",
        method: "POST",
        body: tier,
      }),
      invalidatesTags: ["MarkupTiers"],
    }),

    /** Updates a markup tier. */
    updateMarkupTier: build.mutation<void, Omit<IMarkupTier, "id">>({
      query: (tier) => ({
        url: "/api/trade/v1/markup-tier",
        method: "PUT",
        body: tier,
      }),
      invalidatesTags: ["MarkupTiers"],
    }),

    /** Deletes a markup tier by code. */
    deleteMarkupTier: build.mutation<void, string>({
      query: (tierCode) => ({
        url: "/api/trade/v1/markup-tier",
        method: "DELETE",
        params: { tier_code: tierCode },
      }),
      invalidatesTags: ["MarkupTiers"],
    }),

    // ── Account Tiers ─────────────────────────────────────────────────

    /** Fetches all account-tier assignments. */
    getAccountTiers: build.query<IClstAccountTier[], void>({
      query: () => ({ url: "/api/trade/v1/clst-account-tier" }),
      providesTags: ["AccountTiers"],
    }),

    /** Fetches markup tier options (code + display_name) for account-tier dropdowns. */
    getMarkupTierOptions: build.query<MarkupTierOption[], void>({
      query: () => ({ url: "/api/trade/v1/markup-tier" }),
      transformResponse: (response: IMarkupTier[]) =>
        (response ?? []).map((t) => ({
          code: t.code,
          display_name: t.display_name,
        })),
      providesTags: ["MarkupTiers"],
    }),

    /** Creates an account-tier assignment. */
    createAccountTier: build.mutation<void, Omit<IClstAccountTier, "id">>({
      query: (tier) => ({
        url: "/api/trade/v1/clst-account-tier",
        method: "POST",
        body: tier,
      }),
      invalidatesTags: ["AccountTiers"],
    }),

    /** Updates an account-tier assignment. */
    updateAccountTier: build.mutation<void, Omit<IClstAccountTier, "id">>({
      query: (tier) => ({
        url: "/api/trade/v1/clst-account-tier",
        method: "PUT",
        body: tier,
      }),
      invalidatesTags: ["AccountTiers"],
    }),

    /** Deletes an account-tier assignment. */
    deleteAccountTier: build.mutation<void, string>({
      query: (clstAccount) => ({
        url: "/api/trade/v1/clst-account-tier",
        method: "DELETE",
        params: { clst_account: clstAccount },
      }),
      invalidatesTags: ["AccountTiers"],
    }),
  }),
});

export const {
  useGetMarkupTiersQuery,
  useCreateMarkupTierMutation,
  useUpdateMarkupTierMutation,
  useDeleteMarkupTierMutation,
  useGetAccountTiersQuery,
  useGetMarkupTierOptionsQuery,
  useCreateAccountTierMutation,
  useUpdateAccountTierMutation,
  useDeleteAccountTierMutation,
} = tradeEndpoints;
